import React, {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import classNames from 'classnames';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

import wrap from 'wraptile';
import useStore from '@pluto/use-store-hook';
import handleUnmount from '@pluto/handle-unmount';
import Table from '@pluto/table';
import IngestReportItem from './IngestReportItem';
import {getStatusColorClass} from './IngestReportStyle';
import './IngestReport.scss';
import {ReportStatus, ValidationReportItem} from './IngestReportTypes';

interface IngestReportContainerProps {
    ingestReport?: any | null;
    setReport: (id: number) => void;
    resetReport?: () => void;
}

export default function IngestReportContainer(props: IngestReportContainerProps) {
    const {
        ingestReport,
        setReport,
        resetReport,
    } = props;
    
    const [storeState] = useStore();
    const {id: paramId} = useParams<{id: string}>();
    const id = storeState?.qcTools.qcToolsMetadata.general.contentId || paramId; // get contentItem id from store
    const fetchOrder = onEffect({
        setReport,
        id,
    });
    
    useEffect(fetchOrder, [
        setReport,
        id,
    ]);
    
    const onUnmount = handleUnmount(resetReport);
    
    useEffect(onUnmount, []);
    
    if (!ingestReport)
        return <CircularProgress />;
    
    const {
        validationReportItems,
        date,
        validationStatus,
        warningCount,
        errorCount,
        passedCount,
    } = ingestReport;
    const titleStyle = {
        color: getStatusColorClass(validationStatus as ReportStatus),
    };
    
    return (
        <Box className="ingest-report">
            <Box className="ingest-report__info">
                <Box className="ingest-report__info-summary">
                    <Typography
                        variant="h4"
                        className="ingest-report__info-summary-heading"
                        style={titleStyle}
                    >
                        {validationStatus}
                    </Typography>
                    <IngestReportItem
                        text={passedCount}
                        type="Passed"
                    />
                    <IngestReportItem
                        text={warningCount}
                        type="Warning"
                    />
                    <IngestReportItem
                        text={errorCount}
                        type="Failed"
                    />
                </Box>
                <Typography paragraph>Report date: {date}</Typography>
            </Box>
            <Table
                data={validationReportItems}
                columns={tableColumns}
            />
        </Box>
    );
}

export const renderTableResult = (report: ValidationReportItem) => {
    const {result} = report;
    const classes = classNames(getStatusColorClass(result));
    
    return (
        <span className={classes}>
            {result}
        </span>
    );
};

export const tableColumns = [{
    dataKey: 'title',
    title: 'Validation rule',
}, {
    dataKey: 'description',
    title: 'Description',
}, {
    dataKey: 'result',
    title: 'Result',
    render: renderTableResult,
}];

const onEffect = wrap(({setReport, id}:any) => {
    if (id) {
        setReport(id);
    }
});

IngestReportContainer.defaultProps = {
    ingestReport: null,
    resetReport: undefined,
};
